import { useQueries } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { ButtonPropsWithLabel } from 'src/components/Dialog';
import { Stack } from 'src/components/Stack';
import { useSession } from 'src/contexts/SessionContext';
import {
  fetchInvoices,
  isPayableOpenInvoice,
  pastDueInvoicesQueryKey,
} from 'src/models/payment/payment';
import { isTrainer } from 'src/models/user/utils';

import { ActionsList } from './ActionsList';
import { CheckinPastDuePaymentDialog } from './CheckinPastDuePaymentDialog';
import { CheckinPayBalanceDialog } from './CheckinPayBalanceDialog';
import { SessionContent } from './SessionContent';
import { UserContent } from './UserContent';
import { CheckinBlockerContentProps, CheckinBlockerUser } from './_types';

export type { CheckinBlockerContentProps } from './_types';

function hasUnresolvedBillingBlocker(user: CheckinBlockerUser) {
  return user.blockers.some(
    (blocker) => blocker.type === 'billing_blocked' && !blocker.resolved
  );
}

/**
 * Shared blocking-reason body used by both the CheckinBlockerDialog (React)
 * and future fullscreen/iframe pages (embedded in Ember).
 *
 * This component is intentionally presentation-only — keep host-specific
 * chrome (dialog padding, page layout, scroll wrappers) in the consumer.
 */
export function CheckinBlockerContent({
  users,
  showUserDetails = false,
  showUserName = true,
  blockingReasonVariant = 'checkin',
  onOverride,
  onBillingResolved,
  sessionDetails,
  fillAvailableSpace = false,
}: CheckinBlockerContentProps) {
  const { t } = useTranslation('translation', { keyPrefix: 'checkinBlocker' });
  const { user: sessionUser } = useSession();
  const [paymentDialogUserId, setPaymentDialogUserId] = useState<string>();
  const isStaffFlow = isTrainer(sessionUser) || !!onOverride;

  const billingBlockedUserIds = [
    ...new Set(
      users.filter(hasUnresolvedBillingBlocker).map((u) => String(u.id))
    ),
  ];

  const pastDueInvoiceQueries = useQueries({
    queries: billingBlockedUserIds.map((userId) => ({
      queryKey: pastDueInvoicesQueryKey(userId),
      queryFn: () =>
        fetchInvoices({
          userId,
          per: 100,
          filters: { pastDue: true },
        }),
      enabled: billingBlockedUserIds.length > 0,
    })),
  });

  const userIdHasPayablePastDue = (userId: string) => {
    const id = String(userId);
    const idx = billingBlockedUserIds.indexOf(id);
    if (idx < 0) return false;
    const q = pastDueInvoiceQueries[idx];
    if (!q?.data || q.isError) return false;
    return q.data.invoices.some(isPayableOpenInvoice);
  };

  const getPastDueInvoiceQuery = (userId: string) => {
    const idx = billingBlockedUserIds.indexOf(String(userId));
    if (idx < 0) return undefined;
    return pastDueInvoiceQueries[idx];
  };

  const isPastDueInvoiceInfoLoading = (userId: string) => {
    const q = getPastDueInvoiceQuery(userId);
    if (!q || q.isError) return false;
    return q.isPending || (q.isFetching && q.data === undefined);
  };

  const shouldShowPayBalanceAction = (userId: string) => {
    const q = getPastDueInvoiceQuery(userId);
    if (!q || q.isError) return false;
    if (isPastDueInvoiceInfoLoading(userId)) return true;
    return userIdHasPayablePastDue(userId);
  };

  const hasMultipleUsers = users.length > 1;
  const firstUser = users[0];
  const unresolvedBlockerCount = users.reduce(
    (count, user) =>
      count + user.blockers.filter((blocker) => !blocker.resolved).length,
    0
  );
  const hasMultipleBlockers = unresolvedBlockerCount > 1;

  const getGlobalOverrideLabel = () =>
    hasMultipleUsers || hasMultipleBlockers
      ? t('actions.overrideAndCheckInAll')
      : t('actions.overrideAndCheckIn');

  const buildPayBalanceAction = (
    user: CheckinBlockerUser,
    loading: boolean,
    inline: boolean = false
  ): ButtonPropsWithLabel & { inline?: boolean } => ({
    label: t('actions.updatePaymentMethod'),
    variant: inline ? 'bordered' : 'prominent',
    color: inline ? 'neutral' : 'primary',
    loading,
    disabled: loading,
    onClick: () => setPaymentDialogUserId(user.id),
  });

  const buildOverrideAction = (
    user: CheckinBlockerUser,
    blocker: CheckinBlockerUser['blockers'][number],
    blockerIndex: number
  ): ButtonPropsWithLabel => ({
    label: t('actions.override'),
    variant: 'text',
    color: 'primary',
    onClick: () =>
      void onOverride?.({
        scope: 'blocker',
        user,
        blocker,
        blockerIndex,
      }),
  });

  const globalActions: ButtonPropsWithLabel[] | undefined = (() => {
    if (!firstUser) return undefined;

    if (hasMultipleUsers) {
      return onOverride
        ? [
            {
              label: getGlobalOverrideLabel(),
              variant: 'tertiary',
              onClick: () => void onOverride({ scope: 'all' }),
            },
          ]
        : undefined;
    }

    const actions: ButtonPropsWithLabel[] = [];

    if (onOverride) {
      actions.push({
        label: getGlobalOverrideLabel(),
        variant: 'tertiary',
        color: 'neutral',
        onClick: () => void onOverride({ scope: 'user', user: firstUser }),
      });
    }

    if (
      hasUnresolvedBillingBlocker(firstUser) &&
      shouldShowPayBalanceAction(String(firstUser.id))
    ) {
      actions.push(
        buildPayBalanceAction(
          firstUser,
          isPastDueInvoiceInfoLoading(String(firstUser.id))
        )
      );
    }

    return actions.length > 0 ? actions : undefined;
  })();

  const getBlockerActions = (
    user: CheckinBlockerUser,
    blocker: CheckinBlockerUser['blockers'][number],
    blockerIndex: number
  ): ButtonPropsWithLabel[] | undefined => {
    if (blocker.resolved) return undefined;

    const actions: ButtonPropsWithLabel[] = [];
    const userUnresolvedBlockerCount = user.blockers.filter(
      (item) => !item.resolved
    ).length;
    const showPerBlockerOverride =
      !!onOverride && (hasMultipleUsers || user.blockers.length > 1);

    if (blocker.type === 'waiver_required' && blocker.documentUrl) {
      actions.push({
        label: t('actions.viewDocument'),
        variant: 'prominent',
        onClick: () => {
          window.open(blocker.documentUrl, '_blank', 'noopener,noreferrer');
        },
      });
    }

    if (
      blocker.type === 'billing_blocked' &&
      shouldShowPayBalanceAction(String(user.id)) &&
      (hasMultipleUsers || userUnresolvedBlockerCount > 1)
    ) {
      actions.push(
        buildPayBalanceAction(
          user,
          isPastDueInvoiceInfoLoading(String(user.id)),
          true
        )
      );
    }

    if (showPerBlockerOverride) {
      actions.push(buildOverrideAction(user, blocker, blockerIndex));
    }

    return actions.length > 0 ? actions : undefined;
  };

  const PastDuePaymentDialog = isStaffFlow
    ? CheckinPastDuePaymentDialog
    : CheckinPayBalanceDialog;

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        width: '100%',
        ...(fillAvailableSpace && {
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          minHeight: '100%',
        }),
      }}
    >
      <Stack
        alignItems="stretch"
        spacing={3}
        sx={{
          pb: 2,
          pt: fillAvailableSpace ? 2 : 1,
          px: 3,
          ...(fillAvailableSpace && {
            flex: 1,
            minHeight: 0,
          }),
        }}
      >
        {sessionDetails ? (
          <SessionContent
            users={users}
            sessionDetails={sessionDetails}
            actions={globalActions}
            getBlockerActions={getBlockerActions}
            fillAvailableSpace={fillAvailableSpace}
          />
        ) : (
          <>
            <UserContent
              users={users}
              getBlockerActions={getBlockerActions}
              showUserDetails={showUserDetails}
              showUserName={showUserName}
              variant={blockingReasonVariant}
            />
            {!!globalActions && <ActionsList actions={globalActions} />}
          </>
        )}
      </Stack>
      <PastDuePaymentDialog
        open={!!paymentDialogUserId}
        onClose={() => setPaymentDialogUserId(undefined)}
        onPaymentResolved={() => {
          if (paymentDialogUserId) {
            onBillingResolved?.(paymentDialogUserId);
          }
        }}
        userId={paymentDialogUserId ?? ''}
      />
    </Box>
  );
}
